// src/components/AllSchoolsForParents.js

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { getAllSchoolParent } from "../common/SchoolAPI";
import "../css/AllSchoolsForParent.css";

export default function AllSchoolsForParents() {
  const [schools, setSchools] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  const navigate = useNavigate();

  /* ======================================================
     FETCH SCHOOLS
  ====================================================== */

  useEffect(() => {
    const fetchSchools = async () => {
      try {
        setIsLoading(true);
        setError("");

        const response = await getAllSchoolParent();

        setSchools(
          Array.isArray(response)
            ? response
            : []
        );
      } catch (err) {
        console.error(
          "Unable to load schools:",
          err
        );

        setError(
          "We couldn't load the school network right now. Please try again."
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchSchools();
  }, []);

  /* ======================================================
     FILTER
  ====================================================== */

  const filteredSchools = useMemo(() => {
    const search =
      searchTerm.trim().toLowerCase();

    if (!search) {
      return schools;
    }

    return schools.filter((school) => {
      const name =
        school?.schoolName
          ?.toLowerCase() || "";

      const address =
        school?.schoolAddress
          ?.toLowerCase() || "";

      return (
        name.includes(search) ||
        address.includes(search)
      );
    });
  }, [schools, searchTerm]);

  /* ======================================================
     ACTIONS
  ====================================================== */

  const handleBack = () => {
    navigate("/");
  };

  const handleViewStudents = (schoolId) => {
    navigate(
      `/student-by-school/${schoolId}`
    );
  };

  return (
    <main className="ms-network-page">
      {/* =====================================================
          HERO
      ===================================================== */}

      <section className="ms-network-hero">
        <div className="ms-network-grid-bg" />

        <div className="ms-network-glow ms-network-glow-blue" />
        <div className="ms-network-glow ms-network-glow-yellow" />

        <div className="ms-network-container">
          <div className="ms-network-hero-layout">
            {/* LEFT */}
            <div className="ms-network-hero-copy">
              <button
                type="button"
                className="ms-network-back"
                onClick={handleBack}
              >
                <span>←</span>
                Back to Home
              </button>

              <div className="ms-network-kicker">
                <span />
                OUR SCHOOL NETWORK
              </div>

              <h1>
                Learning together with
                <span> partner schools.</span>
              </h1>

              <p>
                Explore schools connected with
                MindSpark and view students from
                each school in the network.
              </p>
            </div>

            {/* RIGHT SUMMARY */}
            <div className="ms-network-summary">
              <div className="ms-network-summary-top">
                <div>
                  <small>
                    MINDSPARK NETWORK
                  </small>

                  <strong>
                    Connected learning
                  </strong>
                </div>

                <span className="ms-network-status">
                  <i />
                  Active
                </span>
              </div>

              <div className="ms-network-summary-number">
                <strong>
                  {String(
                    schools.length
                  ).padStart(2, "0")}
                </strong>

                <span>
                  Schools available
                </span>
              </div>

              <div className="ms-network-summary-line" />

              <p>
                Learning Through Technology
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* =====================================================
          SCHOOL DIRECTORY
      ===================================================== */}

      <section className="ms-network-directory">
        <div className="ms-network-container">
          {/* DIRECTORY HEADER */}

          <div className="ms-network-directory-header">
            <div>
              <div className="ms-network-kicker">
                <span />
                SCHOOL DIRECTORY
              </div>

              <h2>
                Find your
                <span> school.</span>
              </h2>

              <p>
                Select a school to view the
                students connected with it.
              </p>
            </div>

            {/* SEARCH */}
            <div className="ms-network-search-wrap">
              <label
                htmlFor="school-search"
                className="ms-network-search-label"
              >
                Search schools
              </label>

              <div className="ms-network-search">
                <div
                  className="ms-network-search-icon"
                  aria-hidden="true"
                >
                  <svg viewBox="0 0 24 24">
                    <circle
                      cx="11"
                      cy="11"
                      r="6"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                    />

                    <path
                      d="m16 16 4 4"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                    />
                  </svg>
                </div>

                <input
                  id="school-search"
                  type="search"
                  value={searchTerm}
                  onChange={(event) =>
                    setSearchTerm(
                      event.target.value
                    )
                  }
                  placeholder="Search by school or address..."
                />

                {searchTerm && (
                  <button
                    type="button"
                    className="ms-network-clear"
                    onClick={() =>
                      setSearchTerm("")
                    }
                    aria-label="Clear search"
                  >
                    ×
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* =================================================
              LOADING
          ================================================= */}

          {isLoading && (
            <div className="ms-network-loading">
              <div className="ms-network-loader">
                <span />
                <span />
                <span />
              </div>

              <strong>
                Loading schools
              </strong>

              <p>
                Preparing the MindSpark school
                network...
              </p>
            </div>
          )}

          {/* =================================================
              ERROR
          ================================================= */}

          {!isLoading && error && (
            <div className="ms-network-state-card error">
              <div className="ms-network-state-icon">
                !
              </div>

              <h3>
                Unable to load schools
              </h3>

              <p>
                {error}
              </p>

              <button
                type="button"
                onClick={() =>
                  window.location.reload()
                }
              >
                Try Again
              </button>
            </div>
          )}

          {/* =================================================
              SCHOOL GRID
          ================================================= */}

          {!isLoading &&
            !error &&
            filteredSchools.length > 0 && (
              <>
                <div className="ms-network-results-bar">
                  <p>
                    Showing{" "}
                    <strong>
                      {
                        filteredSchools.length
                      }
                    </strong>{" "}
                    {filteredSchools.length ===
                    1
                      ? "school"
                      : "schools"}
                  </p>

                  {searchTerm && (
                    <span>
                      Results for “
                      {searchTerm}”
                    </span>
                  )}
                </div>

                <div className="ms-network-school-grid">
                  {filteredSchools.map(
                    (school, index) => {
                      const accentClasses = [
                        "yellow",
                        "blue",
                        "purple",
                        "teal",
                      ];

                      const accent =
                        accentClasses[
                          index %
                            accentClasses.length
                        ];

                      return (
                        <article
                          key={school.id}
                          className={`ms-network-school-card ${accent}`}
                        >
                          {/* TOP */}
                          <div className="ms-network-card-top">
                            <div className="ms-network-school-icon">
                              <svg
                                viewBox="0 0 24 24"
                                aria-hidden="true"
                              >
                                <path
                                  d="M3 10 12 5l9 5-9 5-9-5Z"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="1.7"
                                  strokeLinejoin="round"
                                />

                                <path
                                  d="M6 13v5M18 13v5M4 19h16"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="1.7"
                                  strokeLinecap="round"
                                />

                                <path
                                  d="M10 14.5V19M14 14.5V19"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="1.7"
                                  strokeLinecap="round"
                                />
                              </svg>
                            </div>

                            <span className="ms-network-card-number">
                              {String(
                                index + 1
                              ).padStart(
                                2,
                                "0"
                              )}
                            </span>
                          </div>

                          {/* SCHOOL INFO */}
                          <div className="ms-network-card-content">
                            <span className="ms-network-card-label">
                              PARTNER SCHOOL
                            </span>

                            <h3>
                              {
                                school.schoolName
                              }
                            </h3>

                            <div className="ms-network-address">
                              <svg
                                viewBox="0 0 24 24"
                                aria-hidden="true"
                              >
                                <path
                                  d="M12 21s6-5.3 6-11a6 6 0 1 0-12 0c0 5.7 6 11 6 11Z"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="1.7"
                                />

                                <circle
                                  cx="12"
                                  cy="10"
                                  r="2"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="1.7"
                                />
                              </svg>

                              <p>
                                {school.schoolAddress ||
                                  "Address not available"}
                              </p>
                            </div>
                          </div>

                          {/* ACTION */}
                          <div className="ms-network-card-footer">
                            <span>
                              View school students
                            </span>

                            <button
                              type="button"
                              onClick={() =>
                                handleViewStudents(
                                  school.id
                                )
                              }
                            >
                              View Students

                              <span>
                                →
                              </span>
                            </button>
                          </div>

                          <div className="ms-network-card-accent" />
                        </article>
                      );
                    }
                  )}
                </div>
              </>
            )}

          {/* =================================================
              EMPTY
          ================================================= */}

          {!isLoading &&
            !error &&
            filteredSchools.length === 0 && (
              <div className="ms-network-state-card">
                <div className="ms-network-state-icon">
                  <svg
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      d="M3 10 12 5l9 5-9 5-9-5Z"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.7"
                    />

                    <path
                      d="M7 14v5h10v-5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.7"
                    />
                  </svg>
                </div>

                <h3>
                  {searchTerm
                    ? "No matching school found"
                    : "No schools available"}
                </h3>

                <p>
                  {searchTerm
                    ? "Try searching with a different school name or location."
                    : "Schools will appear here when they are available."}
                </p>

                {searchTerm && (
                  <button
                    type="button"
                    onClick={() =>
                      setSearchTerm("")
                    }
                  >
                    Clear Search
                  </button>
                )}
              </div>
            )}
        </div>
      </section>
    </main>
  );
}
